import { defineStore } from 'pinia'

export const useAppStore = defineStore('app', {
  state: () => ({
    // Estado da rede
    networkStatus: {
      connected: true,
      synced: true,
      blockHeight: 0,
      latestBlockTime: null,
      activeValidators: 0,
      totalValidators: 0,
      tps: 0,
      gasPrice: 5,
      lastBlockProducer: null,
      networkLoad: 0,
      networkName: 'QuarnX-Devnet',
      chainId: 3,
      currency: 'tKinX'
    },
    
    // Estado do proxy API
    apiProxy: {
      baseUrl: 'http://localhost:3000',
      connected: false,
      lastUpdated: null,
      cacheStatus: null,
      activeEndpoints: []
    },
    
    // Preferências do usuário
    userPreferences: {
      darkMode: true,
      locale: 'pt-BR',
      dashboardLayout: 'default',
      expandedDetails: false,
      autoRefresh: true,
      refreshInterval: 15, // segundos
    },
    
    // Notificações do sistema
    notifications: [],
    
    // Estado da busca universal
    searchHistory: [],
    
    // Indicador de carregamento global
    loading: false
  }),
  
  getters: {
    isNetworkSynced: (state) => state.networkStatus.synced,
    currentBlockHeight: (state) => state.networkStatus.blockHeight,
    isApiConnected: (state) => state.apiProxy.connected,
    isDarkMode: (state) => state.userPreferences.darkMode,
    formattedBlockHeight: (state) => state.networkStatus.blockHeight.toLocaleString('pt-BR'),
    currentNotifications: (state) => state.notifications.filter(n => !n.read).slice(0, 5),
    hasUnreadNotifications: (state) => state.notifications.some(n => !n.read),
    /** Returns the network currency symbol (tKinX for devnet, KinX for mainnet) */
    coinSymbol: (state) => state.networkStatus.currency || 'tKinX',
    isDevnet: (state) => state.networkStatus.chainId === 3,
    isTestnet: (state) => state.networkStatus.chainId === 2,
    isMainnet: (state) => state.networkStatus.chainId === 1
  },
  
  actions: {
    // Ações relacionadas à rede
    updateNetworkStatus(status) {
      this.networkStatus = { ...this.networkStatus, ...status }
    },
    
    // Ações relacionadas ao proxy API
    setApiConnection(status) {
      this.apiProxy.connected = status
      this.apiProxy.lastUpdated = new Date()
      
      if (status) {
        this.addNotification({
          type: 'success',
          message: 'Conexão com API estabelecida',
          timeout: 3000
        })
      } else {
        this.addNotification({
          type: 'error',
          message: 'Conexão com API perdida',
          timeout: 0, // Sem timeout - precisa ser fechada manualmente
          important: true
        })
      }
    },
    
    refreshApiCache() {
      this.loading = true
      
      // Aqui iria a lógica para fazer uma chamada à API para limpar o cache
      // Apenas simulando para exemplo
      setTimeout(() => {
        this.apiProxy.cacheStatus = 'refreshed'
        this.apiProxy.lastUpdated = new Date()
        this.loading = false
        
        this.addNotification({
          type: 'success',
          message: 'Cache atualizado com sucesso',
          timeout: 3000
        })
      }, 1000)
    },
    
    // Ações relacionadas a preferências do usuário
    toggleDarkMode() {
      this.userPreferences.darkMode = !this.userPreferences.darkMode
    },
    
    setLocale(locale) {
      this.userPreferences.locale = locale
    },
    
    setDashboardLayout(layout) {
      this.userPreferences.dashboardLayout = layout
    },
    
    toggleExpandedDetails() {
      this.userPreferences.expandedDetails = !this.userPreferences.expandedDetails
    },
    
    toggleAutoRefresh() {
      this.userPreferences.autoRefresh = !this.userPreferences.autoRefresh
    },
    
    setRefreshInterval(seconds) {
      this.userPreferences.refreshInterval = seconds
    },
    
    // Ações relacionadas a notificações
    addNotification(notification) {
      const id = Date.now().toString()
      this.notifications.unshift({
        id,
        read: false,
        timestamp: new Date(),
        ...notification
      })
      
      // Remove notificações após timeout, se definido
      if (notification.timeout > 0) {
        setTimeout(() => {
          this.removeNotification(id)
        }, notification.timeout)
      }
      
      return id
    },
    
    markNotificationAsRead(id) {
      const notification = this.notifications.find(n => n.id === id)
      if (notification) {
        notification.read = true
      }
    },
    
    removeNotification(id) {
      const index = this.notifications.findIndex(n => n.id === id)
      if (index !== -1) {
        this.notifications.splice(index, 1)
      }
    },
    
    clearAllNotifications() {
      this.notifications = []
    },
    
    // Ações relacionadas à busca
    addToSearchHistory(query) {
      // Evita duplicatas no histórico
      if (!this.searchHistory.includes(query)) {
        this.searchHistory.unshift(query)
        
        // Limita o histórico a 10 itens
        if (this.searchHistory.length > 10) {
          this.searchHistory.pop()
        }
      }
    },
    
    clearSearchHistory() {
      this.searchHistory = []
    },
    
    // Controle de estado de loading global
    setLoading(status) {
      this.loading = status
    },

    // Compatibility alias for components using showNotification({ color, message, timeout })
    showNotification({ color, message, timeout = 5000 } = {}) {
      const typeMap = { success: 'success', error: 'error', info: 'info', warning: 'warning' }
      this.addNotification({
        type: typeMap[color] || 'info',
        message,
        timeout
      })
    }
  }
})
